import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { GraduationCap, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import {
  openRazorpayCheckout,
  createOrder,
  verifyPayment,
  loadRazorpayScript,
  validateCoupon,
  redeemCouponRegistration,
} from "../lib/razorpay";
import { PROGRAMS } from "./ProgramsPage";
import { SUBJECTS, getDomains } from "../data/subjects";
import { UNIVERSITIES, getColleges } from "../data/universities";
import BrandLogo from "../components/BrandLogo";

const initialForm = {
  // personal
  fullName: "", email: "", phone: "", gender: "", dateOfBirth: "",
  parentName: "",
  // academic
  universityId: "", university: "", collegeName: "", college: "",
  universityRollNumber: "", universityRegNumber: "",
  degree: "", department: "", classSemester: "", academicSession: "",
  // subject + domain (compulsory)
  subjectId: "", domain: "",
  // internship
  selectedProgramId: "", mode: "Online",
  // auth
  password: "",
};

const DEGREE_OPTIONS = ["UG", "PG", "Diploma"];
const DEPARTMENT_OPTIONS = [
  "Arts (Humanities)",
  "Science",
  "Commerce",
  "Engineering",
  "Management",
  "Computer Applications",
  "Education",
  "Law",
  "Pharmacy",
  "Other",
];
const SEMESTER_OPTIONS = [
  "1st Semester", "2nd Semester", "3rd Semester", "4th Semester",
  "5th Semester", "6th Semester", "7th Semester", "8th Semester",
];
const SESSION_OPTIONS = ["2023-2027", "2024-2028"];
const DEFAULT_COURSE_PRICE_INR = 315;
const EXCEPTION_COURSE_PRICE_INR = 515;
const ABR_EXCEPTION_SUBJECTS = new Set([
  "mathematics",
  "math",
  "maths",
  "zoology",
  "botany",
  "chemistry",
  "physics",
]);
const ABR_EXCEPTION_SUBJECT_IDS = new Set([
  "mathematics",
  "zoology",
  "botany",
  "chemistry",
  "physics",
]);
const ABR_COLLEGE_NAMES = new Set([
  "awadhoot bhagwan ram college sasaram",
]);
const FULL_PRICE_COLLEGE_NAMES = new Set([
  "swami shivanand jee tirth college sasaram rohtas",
  "girish narayan mishra college parsathua rohtas",
  "durgawati mahavidyalaya bichhiya dumari kaimur",
]);

const normalizeCollegeName = (value = "") =>
  String(value).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const normalizeSubjectName = (value = "") =>
  String(value).toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, " ").trim();

const getCoursePriceInr = (collegeName, subjectId, subjectName) => {
  const normalizedCollegeName = normalizeCollegeName(collegeName);
  const normalizedSubjectName = normalizeSubjectName(subjectName);
  const isAbrPremiumSubject =
    ABR_EXCEPTION_SUBJECT_IDS.has(subjectId) || ABR_EXCEPTION_SUBJECTS.has(normalizedSubjectName);

  if (ABR_COLLEGE_NAMES.has(normalizedCollegeName) && isAbrPremiumSubject) {
    return EXCEPTION_COURSE_PRICE_INR;
  }

  if (FULL_PRICE_COLLEGE_NAMES.has(normalizedCollegeName)) {
    return EXCEPTION_COURSE_PRICE_INR;
  }

  return DEFAULT_COURSE_PRICE_INR;
};

const normalizeCouponCode = (value = "") =>
  String(value).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);

const RegisterPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    ...initialForm,
    selectedProgramId: location.state?.selectedProgramId || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponChecking, setCouponChecking] = useState(false);
  const couponScopeRef = useRef({
    email: form.email,
    selectedProgramId: form.selectedProgramId,
  });
  const [platformSettings, setPlatformSettings] = useState({
    registrationEnabled: true,
    paymentsEnabled: true,
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "platform"));
        if (snap.exists()) {
          setPlatformSettings((prev) => ({ ...prev, ...snap.data() }));
        }
      } catch (error) {
        console.warn("Platform settings unavailable:", error?.message);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    if (step === 4 && !couponApplied) {
      loadRazorpayScript();
    }
  }, [couponApplied, step]);

  useEffect(() => {
    const scopeChanged =
      couponScopeRef.current.email !== form.email ||
      couponScopeRef.current.selectedProgramId !== form.selectedProgramId;
    couponScopeRef.current = {
      email: form.email,
      selectedProgramId: form.selectedProgramId,
    };
    if (scopeChanged && couponApplied) {
      setCouponApplied(null);
      setCouponInput("");
    }
  }, [couponApplied, form.email, form.selectedProgramId]);

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) {
      toast.error("Enter a coupon code");
      return;
    }
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Enter a valid email before applying coupon");
      return;
    }
    if (!form.selectedProgramId) {
      toast.error("Select an internship program before applying coupon");
      return;
    }

    setCouponChecking(true);
    try {
      const result = await validateCoupon({
        couponCode: code,
        email: form.email,
        selectedProgramId: form.selectedProgramId,
      });
      setCouponApplied(result.coupon);
      setCouponInput(result.coupon.code);
      toast.success("Coupon applied! Registration is now free.");
    } catch (error) {
      setCouponApplied(null);
      toast.error(error.message || "Invalid coupon code");
    } finally {
      setCouponChecking(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponInput("");
  };

  const handle = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // When subject changes, reset domain
  const onSubjectChange = (e) => {
    setForm((p) => ({ ...p, subjectId: e.target.value, domain: "" }));
  };

  // When university changes, reset college
  const onUniversityChange = (e) => {
    const id = e.target.value;
    const u = UNIVERSITIES.find((x) => x.id === id);
    setForm((p) => ({
      ...p,
      universityId: id,
      university: u?.name || "",
      collegeName: "",
      college: "",
    }));
  };

  const selectedProgram = PROGRAMS.find((p) => p.id === form.selectedProgramId);
  const selectedSubject = SUBJECTS.find((s) => s.id === form.subjectId);
  const availableDomains = getDomains(form.subjectId);
  const availableColleges = getColleges(form.universityId);
  const showCollegeText = !form.universityId || availableColleges.length === 0;
  const selectedCollegeName = form.collegeName || form.college;
  const coursePriceInr = getCoursePriceInr(selectedCollegeName, form.subjectId, selectedSubject?.name);

  const validateStep = () => {
    if (step === 1) {
      if (!form.fullName || !form.email || !form.phone || !form.parentName) {
        toast.error("Please fill all required personal details (including Parent / Guardian Name)");
        return false;
      }
      if (!/^\S+@\S+\.\S+$/.test(form.email)) {
        toast.error("Please enter a valid email");
        return false;
      }
      if (!/^\d{10}$/.test(form.phone)) {
        toast.error("Phone must be 10 digits");
        return false;
      }
    }
    if (step === 2) {
      if (!form.universityId || !form.university) {
        toast.error("Please select your University");
        return false;
      }
      const collegeValue = showCollegeText ? form.collegeName : form.college;
      if (!collegeValue) {
        toast.error("Please select / enter your College");
        return false;
      }
      if (!form.universityRollNumber) {
        toast.error("Please enter your University Roll Number");
        return false;
      }
      if (!form.universityRegNumber) {
        toast.error("Please enter your University Registration Number");
        return false;
      }
      if (!form.degree) {
        toast.error("Please select Degree (UG / PG / Diploma)");
        return false;
      }
      if (!form.department) {
        toast.error("Please select Department / Stream");
        return false;
      }
      if (!form.classSemester) {
        toast.error("Please select Class / Semester");
        return false;
      }
      if (!form.academicSession) {
        toast.error("Please select Academic Session");
        return false;
      }
    }
    if (step === 3) {
      if (!form.subjectId) {
        toast.error("Please select your Major Subject (compulsory)");
        return false;
      }
      if (!form.domain) {
        toast.error("Please select your Domain (compulsory)");
        return false;
      }
      if (!form.selectedProgramId) {
        toast.error("Please select an Internship Program (compulsory)");
        return false;
      }
      if (!form.password || form.password.length < 6) {
        toast.error("Password must be at least 6 characters");
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (validateStep()) setStep((s) => Math.min(4, s + 1));
  };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const startPayment = async () => {
    if (!validateStep()) return;
    if (!selectedProgram) return;
    if (platformSettings.registrationEnabled === false) {
      toast.error("Registration is currently closed. Please contact support.");
      return;
    }
    if (!couponApplied && platformSettings.paymentsEnabled === false) {
      toast.error("Payments are currently unavailable. Please contact support.");
      return;
    }

    // FREE registration path via coupon
    if (couponApplied) {
      setSubmitting(true);
      await handleCouponRegistration();
      return;
    }

    setSubmitting(true);
    try {
      const [order, scriptReady] = await Promise.all([
        createOrder({
          receipt: `CC_${Date.now()}`,
          email: form.email,
          collegeName: form.collegeName,
          college: form.college,
          subjectId: form.subjectId,
          subjectName: selectedSubject?.name || "",
        }),
        loadRazorpayScript(),
      ]);
      if (!scriptReady) {
        throw new Error("Razorpay SDK failed to load.");
      }
      const orderAmountInr = Number(order?.amount || 0) / 100;
      if (orderAmountInr !== coursePriceInr) {
        throw new Error("Payment amount mismatch. Please contact support.");
      }

      await openRazorpayCheckout({
        amount: orderAmountInr,
        name: form.fullName,
        email: form.email,
        phone: form.phone,
        description: selectedProgram.name,
        orderId: order?.id,
        onSuccess: async (payment) => {
          await handlePaymentSuccess(payment, order);
        },
        onFailure: (msg) => {
          setSubmitting(false);
          navigate("/payment-failed", { state: { message: msg } });
        },
        onDismiss: () => {
          setSubmitting(false);
          toast.info("Payment cancelled. No data was saved.");
        },
      });
    } catch (e) {
      setSubmitting(false);
      toast.error(e.message || "Payment initialization failed");
    }
  };

  const handleCouponRegistration = async () => {
    try {
      const result = await redeemCouponRegistration({
        couponCode: couponApplied.code,
        registrationData: {
          ...form,
          selectedProgramName: selectedProgram.name,
          subjectName: selectedSubject?.name || "",
        },
      });

      navigate("/payment-success", {
        state: {
          studentId: result.studentId,
          email: form.email.trim(),
          tempPassword: result.existingAccount ? null : form.password,
          courseTitle: selectedProgram.name,
          existingAccount: result.existingAccount,
        },
      });
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Failed to create free registration");
      navigate("/payment-failed", {
        state: { message: e.message, mode: "free" }
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (payment, order) => {
    try {
      const verifyRes = await verifyPayment({
        razorpay_order_id: payment.razorpay_order_id || order?.id || null,
        razorpay_payment_id: payment.razorpay_payment_id,
        razorpay_signature: payment.razorpay_signature || null,
        registrationData: {
          ...form,
          selectedProgramName: selectedProgram.name,
        },
      });

      if (!verifyRes.verified) {
        toast.error("Payment verification failed");
        navigate("/payment-failed", { state: { message: "Verification failed. Please contact support." } });
        return;
      }

      // Cloud Function creates the student, payment, and enrollment records.
      if (verifyRes.studentId) {
        navigate("/payment-success", {
          state: {
            studentId: verifyRes.studentId,
            email: form.email,
            tempPassword: verifyRes.existingAccount ? null : form.password,
            courseTitle: selectedProgram.name,
            existingAccount: verifyRes.existingAccount,
          },
        });
        return;
      }

      throw new Error("Payment verified, but student account was not created. Please contact support.");
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Failed to create account");
      navigate("/payment-failed", {
        state: { message: e.message, mode: "paid" }
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" className="min-w-0 flex-shrink">
            <BrandLogo size="md" sub={false} />
          </Link>
          <Link to="/login" className="shrink-0 rounded-lg border border-slate-200 px-3 py-2 text-right text-xs font-semibold text-slate-700 transition-all hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 sm:px-4 sm:text-sm">
            Already have an account? Login
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Student Registration</h1>
        <p className="mt-2 text-slate-600">Complete all steps to enroll in your internship program.</p>

        {platformSettings.registrationEnabled === false && (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-900">
            Registration is currently closed. Please contact support for assistance.
          </div>
        )}
        {platformSettings.registrationEnabled !== false && platformSettings.paymentsEnabled === false && (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-900">
            Payments are currently unavailable. Please contact support before submitting registration.
          </div>
        )}

        {/* Stepper */}
        <div className="mt-8 grid grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] items-start gap-2 sm:gap-4" data-testid="register-stepper">
          {["Personal Info", "Academic Info", "Training Program", "Payment"].map((s, i) => {
            const n = i + 1;
            const done = step > n;
            const active = step === n;
            return (
              <React.Fragment key={s}>
                <div className="flex min-w-0 flex-col items-center">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold sm:h-11 sm:w-11 ${
                    done ? "bg-green-600 text-white" : active ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"
                  }`}>
                    {done ? <CheckCircle2 className="w-4 h-4" /> : n}
                  </div>
                  <div className={`mt-2 min-h-[2.5rem] max-w-[5.5rem] text-center text-xs font-medium leading-5 sm:max-w-[7rem] sm:text-sm ${
                    active ? "text-slate-900" : "text-slate-600"
                  }`}>
                    {s}
                  </div>
                </div>
                {i < 3 && <div className={`mt-5 h-0.5 w-full min-w-6 ${step > n ? "bg-green-600" : "bg-slate-200"}`}></div>}
              </React.Fragment>
            );
          })}
        </div>

        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Personal Details</h2>
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Full Name *" testid="reg-fullname-input" value={form.fullName} onChange={handle("fullName")} />
                <Field label="Parent / Guardian Name *" testid="reg-parentname-input" value={form.parentName} onChange={handle("parentName")} placeholder="Father / Mother / Guardian" />
                <Field label="Email *" type="email" testid="reg-email-input" value={form.email} onChange={handle("email")} />
                <Field label="Phone *" testid="reg-phone-input" value={form.phone} onChange={handle("phone")} placeholder="10 digits" />
                <SelectField label="Gender" testid="reg-gender-select" value={form.gender} onChange={handle("gender")} options={["Male", "Female", "Other"]} />
                <Field label="Date of Birth" type="date" testid="reg-dob-input" value={form.dateOfBirth} onChange={handle("dateOfBirth")} />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Academic Details</h2>
              <div className="grid md:grid-cols-2 gap-5">
                {/* University - dropdown */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">University *</label>
                  <select
                    data-testid="reg-university-select"
                    value={form.universityId}
                    onChange={onUniversityChange}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  >
                    <option value="">-- Select University --</option>
                    {UNIVERSITIES.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>

                {/* College - dropdown if list exists, else text */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">College *</label>
                  {showCollegeText ? (
                    <input
                      type="text"
                      data-testid="reg-college-input"
                      value={form.collegeName}
                      onChange={(e) => setForm({ ...form, collegeName: e.target.value, college: e.target.value })}
                      placeholder={form.universityId ? "Type your college name" : "Select university first"}
                      disabled={!form.universityId}
                      className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:bg-slate-100"
                    />
                  ) : (
                    <select
                      data-testid="reg-college-select"
                      value={form.college}
                      onChange={(e) => setForm({ ...form, college: e.target.value, collegeName: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                    >
                      <option value="">-- Select College --</option>
                      {availableColleges.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  )}
                  {!showCollegeText && (
                    <p className="text-xs text-slate-500 mt-1">{availableColleges.length} colleges available under this university.</p>
                  )}
                </div>

                {/* University Roll & Registration Number */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">University Roll Number *</label>
                  <input
                    type="text"
                    data-testid="reg-uniroll-input"
                    value={form.universityRollNumber}
                    onChange={handle("universityRollNumber")}
                    placeholder="Enter your university roll number"
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">University Registration Number *</label>
                  <input
                    type="text"
                    data-testid="reg-unireg-input"
                    value={form.universityRegNumber}
                    onChange={handle("universityRegNumber")}
                    placeholder="Enter your university registration number"
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>

                {/* Degree */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Degree *</label>
                  <select
                    data-testid="reg-degree-select"
                    value={form.degree}
                    onChange={handle("degree")}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  >
                    <option value="">-- Select Degree --</option>
                    {DEGREE_OPTIONS.map((d) => (<option key={d} value={d}>{d}</option>))}
                  </select>
                </div>

                {/* Department/Stream - depends on college */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Department / Stream *</label>
                  <select
                    data-testid="reg-department-select"
                    value={form.department}
                    onChange={handle("department")}
                    disabled={!(form.collegeName || form.college)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    <option value="">{(form.collegeName || form.college) ? "Select Department" : "Please select a college first"}</option>
                    {DEPARTMENT_OPTIONS.map((d) => (<option key={d} value={d}>{d}</option>))}
                  </select>
                </div>

                {/* Class/Semester */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Class / Semester *</label>
                  <select
                    data-testid="reg-semester-select"
                    value={form.classSemester}
                    onChange={handle("classSemester")}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  >
                    <option value="">-- Select Semester --</option>
                    {SEMESTER_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                </div>

                {/* Academic Session - depends on department */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Academic Session *</label>
                  <select
                    data-testid="reg-session-select"
                    value={form.academicSession}
                    onChange={handle("academicSession")}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  >
                    <option value="">-- Select Academic Session --</option>
                    {SESSION_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                  <p className="mt-1 text-xs text-slate-500">Select your academic session.</p>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Training Program Info</h2>
              <p className="text-sm text-slate-600 -mt-2">Choose your major subject, domain, and internship program in one place.</p>

              {/* 1. Major Subject (compulsory) */}
              <div className="bg-white border-2 border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">1</span>
                  <label className="block text-sm font-semibold text-slate-900">Major Subject *</label>
                </div>
                <select
                  data-testid="reg-subject-select"
                  value={form.subjectId}
                  onChange={onSubjectChange}
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  <option value="">-- Select Major Subject --</option>
                  {SUBJECTS.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {selectedSubject && (
                  <p className="text-xs text-slate-500 mt-2">
                    {selectedSubject.domains.length} domain option(s) available for <strong>{selectedSubject.name}</strong>.
                  </p>
                )}
              </div>

              {/* 2. Domain (compulsory, depends on Subject) */}
              <div className="bg-white border-2 border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">2</span>
                  <label className="block text-sm font-semibold text-slate-900">Domain *</label>
                </div>
                <select
                  data-testid="reg-domain-select"
                  value={form.domain}
                  onChange={handle("domain")}
                  disabled={!form.subjectId}
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:bg-slate-100 disabled:text-slate-400"
                >
                  <option value="">{form.subjectId ? "-- Select Domain --" : "Select Major Subject first"}</option>
                  {availableDomains.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              {/* 3. Program/Course (compulsory) - no prices shown */}
              <div className="bg-white border-2 border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">3</span>
                  <label className="block text-sm font-semibold text-slate-900">Training Program *</label>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {PROGRAMS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`reg-program-${p.id}`}
                      onClick={() => setForm((prev) => ({ ...prev, selectedProgramId: p.id }))}
                      className={`text-left p-4 rounded-lg border-2 transition-all ${
                        form.selectedProgramId === p.id
                          ? "border-blue-600 bg-blue-50"
                          : "border-slate-200 hover:border-slate-300 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <p.icon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <span className="font-heading font-semibold text-slate-900 text-sm">{p.name}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">Structured Training</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-5 pt-3 md:grid-cols-2">
                <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-800">
                  Mode: Online only
                </div>
                <Field label="Create Password *" type="password" testid="reg-password-input" value={form.password} onChange={handle("password")} placeholder="Min 6 characters" />
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Review & Pay</h2>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-3 text-sm">
                <ReviewRow label="Name" value={form.fullName} />
                <ReviewRow label="Parent / Guardian" value={form.parentName} />
                <ReviewRow label="Email" value={form.email} />
                <ReviewRow label="Phone" value={form.phone} />
                <div className="border-t border-slate-200 pt-3">
                  <ReviewRow label="University" value={form.university} />
                  <ReviewRow label="College" value={form.collegeName || form.college} />
                  <ReviewRow label="University Roll No." value={form.universityRollNumber} />
                  <ReviewRow label="University Reg. No." value={form.universityRegNumber} />
                  <ReviewRow label="Degree" value={form.degree} />
                  <ReviewRow label="Department" value={form.department} />
                  <ReviewRow label="Class / Semester" value={form.classSemester} />
                  <ReviewRow label="Academic Session" value={form.academicSession} />
                </div>
                <div className="border-t border-slate-200 pt-3">
                  <ReviewRow label="Major Subject" value={selectedSubject?.name} />
                  <ReviewRow label="Domain" value={form.domain} />
                </div>
                <div className="border-t border-slate-200 pt-3">
                  <ReviewRow label="Internship Program" value={selectedProgram?.name} />
                  <ReviewRow label="Duration" value={selectedProgram?.duration} />
                  <ReviewRow label="Mode" value={form.mode} />
                </div>
                <div className="border-t border-slate-200 pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Subtotal</span>
                    <span className={`font-semibold ${couponApplied ? "line-through text-slate-400" : "text-slate-900"}`}>₹{coursePriceInr}</span>
                  </div>
                  {couponApplied && (
                    <div className="flex items-center justify-between text-emerald-700">
                      <span>Coupon ({couponApplied.code})</span>
                      <span className="font-semibold">- ₹{coursePriceInr}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                    <span className="font-heading font-bold text-slate-900 text-lg">Total Payable</span>
                    <span className="font-heading font-bold text-2xl" style={{ color: couponApplied ? "#059669" : "#2563EB" }}>
                      {couponApplied ? "FREE" : `₹${coursePriceInr}`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Coupon code box */}
              <div className="mt-5 bg-white border border-slate-200 rounded-xl p-5">
                <label className="block text-sm font-semibold text-slate-900 mb-2">Have a coupon code?</label>
                {!couponApplied ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      data-testid="reg-coupon-input"
                      value={couponInput}
                      onChange={(e) => setCouponInput(normalizeCouponCode(e.target.value))}
                      placeholder="Enter coupon code"
                      maxLength={10}
                      className="flex-1 bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 uppercase tracking-wider"
                    />
                    <button
                      type="button"
                      onClick={applyCoupon}
                      disabled={couponChecking}
                      data-testid="reg-apply-coupon-btn"
                      className="px-5 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold whitespace-nowrap disabled:opacity-50"
                    >
                      {couponChecking ? "Checking..." : "Apply"}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" data-testid="reg-coupon-applied-box">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      <div>
                        <div className="font-semibold text-emerald-900">{couponApplied.code}</div>
                        <div className="text-xs text-emerald-700">100% off applied - Registration is free</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeCoupon}
                      data-testid="reg-remove-coupon-btn"
                      className="text-xs text-emerald-700 hover:text-emerald-900 font-semibold underline"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-5 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
                <strong>{couponApplied ? "Free Registration:" : "Payment Secure Notice:"}</strong>{" "}
                {couponApplied
                  ? "Your coupon is valid. Click below to complete registration — no payment will be charged."
                  : "Your registration data will be saved only after successful payment & verification. If payment fails, no data is stored — you can safely retry."}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button onClick={prev} disabled={step === 1} data-testid="reg-prev-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium disabled:opacity-30">
              <ArrowLeft className="w-4 h-4" /> Previous
            </button>
            {step < 4 ? (
              <button onClick={next} data-testid="reg-next-btn" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                Next <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={startPayment}
                disabled={submitting}
                data-testid="reg-pay-btn"
                className={`inline-flex items-center gap-2 px-7 py-3 rounded-lg text-white font-semibold disabled:opacity-50 ${
                  couponApplied ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {submitting
                  ? "Processing..."
                  : couponApplied
                    ? "Register FREE & Continue"
                    : `Pay ₹${coursePriceInr} & Register`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, type = "text", value, onChange, placeholder, className = "", testid }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    <input
      type={type}
      data-testid={testid}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    <select
      data-testid={testid}
      value={value}
      onChange={onChange}
      className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
    >
      <option value="">Select...</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const ReviewRow = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-900">{value || "—"}</span>
  </div>
);

export default RegisterPage;
